import { useCallback, useMemo } from 'react'
import type { MediaKind } from '@/entries'
import type { EnrichedMediaResult } from '@/enrichment'
import type { MediaEditValues } from './types'

/**
 * Hook to manage edit form initial values for media preview
 */
export function useMediaPreviewEdit(
  type: MediaKind,
  enrichedData: EnrichedMediaResult | null,
  editedValues?: MediaEditValues,
) {
  const enriched = enrichedData?.enriched

  // Memoize the function to get initial edit values
  const getInitialEditValue = useCallback(
    (field: keyof MediaEditValues): string => {
      if (editedValues && field in editedValues) {
        const val = editedValues[field]
        if (Array.isArray(val)) return val.join(', ')
        if (typeof val === 'number') return val.toString()
        return val || ''
      }
      if (!enriched) return ''
      switch (field) {
        case 'title':
        case 'name':
          return enriched.title || enrichedData?.name || ''
        case 'genres':
          return enriched.genres?.join(', ') || ''
        case 'description':
        case 'bio':
        case 'profile':
          return enriched.description || ''
        case 'year':
          return enriched.year?.toString() || ''
        case 'director':
          return enriched.director || ''
        case 'author':
          return enriched.author || ''
        case 'website':
          return enriched.website || ''
        case 'bpm':
        case 'key':
          // bpm and key are only in editedValues, not in enrichment API response
          return ''
        default:
          return ''
      }
    },
    [editedValues, enriched, enrichedData?.name],
  )

  // Compute initial values using useMemo
  const initialEditValues = useMemo(() => {
    return {
      title:
        type === 'artist' || type === 'label'
          ? getInitialEditValue('name')
          : getInitialEditValue('title'),
      genres: getInitialEditValue('genres'),
      description:
        type === 'artist'
          ? getInitialEditValue('bio')
          : type === 'label'
            ? getInitialEditValue('profile')
            : getInitialEditValue('description'),
      year: getInitialEditValue('year'),
      director: getInitialEditValue('director'),
      author: getInitialEditValue('author'),
      website: getInitialEditValue('website'),
      bpm: getInitialEditValue('bpm'),
      key: getInitialEditValue('key'),
    }
  }, [type, getInitialEditValue])

  return { initialEditValues, getInitialEditValue }
}
